import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Share2, Smartphone, Bell, UserPlus, CheckCircle2, Link2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STEPS = [
  { icon: Smartphone, text: "Karyawan scan QR code ini dengan kamera HP, atau buka link di browser HP" },
  { icon: UserPlus, text: "Login dengan username & password yang Bos buatkan di menu Karyawan" },
  { icon: Smartphone, text: "iPhone: tekan Share lalu 'Add to Home Screen'. Android: otomatis bisa" },
  { icon: Bell, text: "Di Dashboard, tekan 'Aktifkan' pada kartu Notifikasi HP, lalu tekan 'Tes'" },
];

export default function Share() {
  const [savedUrl, setSavedUrl] = useState("");
  const [inputUrl, setInputUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const appUrl = (savedUrl || window.location.origin) + "/login";

  useEffect(() => {
    api.get("/settings/app-url").then(({ data }) => {
      setSavedUrl(data.app_url || "");
      setInputUrl(data.app_url || "");
    }).catch(() => {});
  }, []);

  const saveUrl = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/app-url", { app_url: inputUrl });
      setSavedUrl(data.app_url);
      setInputUrl(data.app_url);
      toast.success(data.app_url ? "Link produksi tersimpan — QR diperbarui" : "Link produksi dihapus — QR kembali mengikuti alamat yang dibuka");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan link");
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopied(true);
      toast.success("Link tersalin — kirim ke karyawan via WhatsApp");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Gagal menyalin link");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "GudangFlow", text: "Buka aplikasi GudangFlow dan login dengan akun Anda:", url: appUrl });
      } catch { /* dibatalkan user */ }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-6 max-w-md mx-auto">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Onboarding</p>
        <h1 className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Bagikan ke Karyawan</h1>
        <p className="mt-1 text-slate-600 text-base">Scan QR code ini dari HP karyawan untuk langsung membuka aplikasi.</p>
      </div>

      <div data-testid="qr-card" className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col items-center">
        <div className="bg-white p-3 rounded-xl border-2 border-slate-900">
          <QRCodeSVG data-testid="app-qr-code" value={appUrl} size={220} level="M" imageSettings={{ src: "/icon-192.png", height: 40, width: 40, excavate: true }} />
        </div>
        <p data-testid="app-url-text" className="mt-4 text-sm font-mono font-semibold text-slate-700 break-all text-center">{appUrl}</p>
        <div className="mt-4 flex gap-2 w-full">
          <Button data-testid="copy-link-btn" variant="outline" className="flex-1" onClick={copyLink}>
            {copied ? <CheckCircle2 size={16} className="mr-2 text-emerald-600" /> : <Copy size={16} className="mr-2" />}
            {copied ? "Tersalin" : "Salin Link"}
          </Button>
          <Button data-testid="share-link-btn" className="flex-1" onClick={shareNative}>
            <Share2 size={16} className="mr-2" />Bagikan
          </Button>
        </div>
      </div>

      <div data-testid="prod-url-card" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-2">
          <Link2 size={16} className="text-blue-700" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Link Produksi (Hasil Deploy)</h2>
        </div>
        <p className="text-sm text-slate-600">
          QR di atas otomatis mengikuti alamat yang sedang dibuka. Setelah aplikasi di-deploy, tempel link produksi di sini sekali agar QR selalu menunjuk link sebenarnya dari HP mana pun.
        </p>
        <div className="flex gap-2">
          <input
            data-testid="prod-url-input"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="https://nama-app-anda.emergent.host"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Button data-testid="prod-url-save-btn" disabled={saving} onClick={saveUrl}>
            <Save size={16} className="mr-1" />Simpan
          </Button>
        </div>
        {!savedUrl && (
          <p data-testid="prod-url-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            Belum ada link produksi tersimpan — QR saat ini menunjuk alamat yang sedang dibuka (preview).
          </p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: "Manrope" }}>Langkah untuk Karyawan</h2>
        <ol className="space-y-3">
          {STEPS.map((s, i) => (
            <li key={i} data-testid={`share-step-${i + 1}`} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><s.icon size={16} /></div>
              <p className="text-sm text-slate-700 pt-1.5">{s.text}</p>
            </li>
          ))}
        </ol>
        <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Pastikan karyawan membuka link hasil <b>deploy</b> (bukan link preview) agar aplikasi selalu aktif dan notifikasi berjalan.
        </p>
      </div>
    </div>
  );
}
